/**
 * language configuration & lazy loading
 * based on: https://kazupon.github.io/vue-i18n/guide/lazy-loading.html
 *
 */
import { createI18n } from 'vue-i18n';

import { Logger } from 'oidc-client-ts';
import { compareStr } from '@/components/utils';
import messages_ar from './ar.json';
import messages_bg from './bg.json';
import messages_cs from './cs.json';
import messages_da from './da.json';
import messages_de from './de.json';
import messages_de_AT from './de-AT.json';
import messages_de_BE from './de-BE.json';
import messages_de_CH from './de-CH.json';
import messages_el from './el.json';
import messages_en from './en.json';
import messages_en_AU from './en-AU.json';
import messages_en_CA from './en-CA.json';
import messages_en_GB from './en-GB.json';
import messages_en_NZ from './en-NZ.json';
import messages_en_ZA from './en-ZA.json';
import messages_es from './es.json';
import messages_es_MX from './es-MX.json';
import messages_et from './et.json';
import messages_fi from './fi.json';
import messages_fr from './fr.json';
import messages_fr_BE from './fr-BE.json';
import messages_fr_CA from './fr-CA.json';
import messages_fr_CH from './fr-CH.json';
import messages_fr_LU from './fr-LU.json';
import messages_he from './he.json';
import messages_hr from './hr.json';
import messages_hu from './hu.json';
import messages_id from './id.json';
import messages_it_CH from './it-CH.json';
import messages_it from './it.json';
import messages_ja from './ja.json';
import messages_kk from './kk.json';
import messages_ko from './ko.json';
import messages_lt from './lt.json';
import messages_lv from './lv.json';
import messages_mk from './mk.json';
import messages_nb from './nb.json';
import messages_nl_BE from './nl-BE.json';
import messages_nl from './nl.json';
import messages_pl from './pl.json';
import messages_pt from './pt.json';
import messages_pt_BR from './pt-BR.json';
import messages_ro from './ro.json';
import messages_ru from './ru.json';
import messages_sk from './sk.json';
import messages_sl from './sl.json';
import messages_sq from './sq.json';
import messages_sr from './sr.json';
import messages_sv from './sv.json';
import messages_th from './th.json';
import messages_tr from './tr.json';
import messages_uk from './uk.json';
import messages_vi from './vi.json';
import messages_zh from './zh.json';

/**
 * IMPORTANT: more specific locale must be before general locale (example: 'pt-BR' must be before 'pt')
 */
const messages = {
  ar: messages_ar,
  bg: messages_bg,
  cs: messages_cs,
  da: messages_da,
  'de-AT': messages_de_AT,
  'de-BE': messages_de_BE,
  'de-CH': messages_de_CH,
  de: messages_de,
  el: messages_el,
  'en-AU': messages_en_AU,
  'en-CA': messages_en_CA,
  'en-GB': messages_en_GB,
  'en-NZ': messages_en_NZ,
  'en-ZA': messages_en_ZA,
  en: messages_en,
  'es-MX': messages_es_MX,
  es: messages_es,
  et: messages_et,
  fi: messages_fi,
  'fr-BE': messages_fr_BE,
  'fr-CA': messages_fr_CA,
  'fr-CH': messages_fr_CH,
  'fr-LU': messages_fr_LU,
  fr: messages_fr,
  he: messages_he,
  hr: messages_hr,
  hu: messages_hu,
  id: messages_id,
  'it-CH': messages_it_CH,
  it: messages_it,
  ja: messages_ja,
  kk: messages_kk,
  ko: messages_ko,
  lt: messages_lt,
  lv: messages_lv,
  mk: messages_mk,
  nb: messages_nb,
  'nl-BE': messages_nl_BE,
  nl: messages_nl,
  pl: messages_pl,
  'pt-BR': messages_pt_BR,
  pt: messages_pt,
  ro: messages_ro,
  ru: messages_ru,
  sk: messages_sk,
  sl: messages_sl,
  sq: messages_sq,
  sr: messages_sr,
  sv: messages_sv,
  th: messages_th,
  tr: messages_tr,
  uk: messages_uk,
  vi: messages_vi,
  zh: messages_zh,
};

/*
    TODO this brakes the validation unfortunately
 * import all language files and put the translations in 'messages'.
 * The order is important: more localized must come first in list. (ex: pt-BR before pt)
 */
/* const messages: Record<string, any> = {};
const languageFiles = import.meta.glob('./*.json', { eager: true });
// eslint-disable-next-line guard-for-in,no-restricted-syntax
for (const path in languageFiles) {
  // Logger.debug('[lang] loaded:', path, languageFiles[path]);
  const locale = path.substring(2, path.length - 5);
  messages[locale] = languageFiles[path];
  // console.info('[locale]', 'locale:', locale);
} */
// Logger.debug('lang', 'messages:', messages);

export const REGEX_LOCALE = /^[a-z]{2}(-[a-zA-Z]{2})?$/;

type SupportedLocale = keyof typeof messages;
export const supportedLocales = Object.keys(messages) as SupportedLocale[];
// console.debug('[locale] supportedLocales:', supportedLocales);

export const getLocaleFromPath = (path: string) => {
  const item = path.startsWith(import.meta.env.BASE_URL) ? 3 : 1;
  const loc = path.split('/')[item];
  return REGEX_LOCALE.test(loc) ? loc : null;
};

export const toSupportedLocale = (loc: string | null | undefined, def: SupportedLocale) => {
  if (!loc || !REGEX_LOCALE.test(loc)) return def;

  // search if it starts with a supported locale
  const index = supportedLocales.findIndex(
    (locale) => loc.toLowerCase().startsWith(locale.toLowerCase()),
  );
  return index < 0 ? def : supportedLocales[index];
};

// not very useful because immediately redirected to OIDCP - look at OpenIdConnectCallback
const getInitialLocale = () => {
  // 1. try 'locale' parameter
  const url = new URLSearchParams(window.location.search);
  let loc = url.get('locale');
  // Logger.debug(`[locale] query-param: ${loc}`)

  // 2. try the path (ex: '/de-ch/members')
  if (!loc) loc = getLocaleFromPath(window.location.pathname);

  loc = toSupportedLocale(loc, 'en');

  Logger.debug('locale', `init-locale:${loc}`);
  return loc;
};

export const i18n = createI18n({
  locale: getInitialLocale(), // set locale
  fallbackLocale: 'en',
  messages,
  //legacy: false TODO see https://vue-i18n.intlify.dev/guide/advanced/composition
});

export const localeDir = (locale: string): 'ltr' | 'rtl' => {
  if (locale === 'ar' || locale === 'he') return 'rtl';
  return 'ltr';
};

/**
 * changes the locale
 */
export const setAppLocale = (locale: string | null) => {
  if (locale) {
    const lang = toSupportedLocale(locale, 'en');
    Logger.info('locale', `req:${locale} set:${lang}`);
    i18n.global.locale = lang;
    document.querySelector('html')?.setAttribute('lang', lang);
    document.querySelector('html')?.setAttribute('dir', localeDir(lang));
  }
  return i18n.global.locale;
};

export interface LanguageOption {
  locale: string,
  name: string
}

export const languageOptions = (transl: (k: string) => string): LanguageOption[] => {
  const items: Array<LanguageOption> = supportedLocales.map((loc) => ({
    locale: loc,
    name: transl(`adm.lang.${loc}`),
  }));

  items.sort((a: LanguageOption, b: LanguageOption) => compareStr(a.name, b.name));

  return items;
};

/**
 * STIHL supported countries
 */
export const supportedCountries = () => Object.keys(messages_en.adm.countries);
